import React from 'react'
import styled from '@emotion/styled'
import strings from 'utils/strings.js'
import { insertIf } from 'utils/types'
import { uniqBy, prop } from 'ramda'
import {
  CHECPLUS_MODULE_TYPES,
  PWM,
  AMPS20,
  FEATURE_MODULE_NAMES_WITH_HARNESSES,
} from 'utils/constants'

import { PWM_MODES_MAPPING } from 'constants/featureOutputs'
import { INTERNAL } from 'constants/featureTypes'
import { EvaluationIconStyled } from 'components/patterns/Icon/IconsStyled'
import {
  isNativeOutput,
  isPinOutput,
  isCanOutput,
  isTimer,
  isDigitalInput,
  isCanInput,
} from 'utils/types/features'

import {
  virtualFuseMask,
  virtualFuseReset,
  polarityLabels,
  keepAwakeLabel,
  powerNetLevels,
  pwmModeTitle,
} from 'i18n/displayConstants/featureOutputs'
import { conditionPolarityLabels } from 'i18n/displayConstants/conditions'
import { bitBytePairLabel } from 'components/feature/utils/bitGrid'
import { translationValue } from 'utils/translations'

const { XMC } = CHECPLUS_MODULE_TYPES

const dutyCycleValue = (dutyCycle) =>
  `${dutyCycle}${strings.duty_cycle_label_append_symbol}`
const frequencyValue = (frequency) =>
  `${frequency}${strings.frequency_label_append_symbol}`
const pwmModeValue = (pwmMode) => `${pwmMode} - ${pwmModeTitle(pwmMode)}`

export const getOutputValues = ({
  current,
  polarity,
  type,
  frequency,
  frequencyMin,
  frequencyMax,
  virtualFuseSetting,
  resetType,
  resetAttempts,
  startBit,
  numberOfBits,
  dutyCycle,
  dutyCycleMin,
  dutyCycleMax,
  isAwake,
  featureModuleType,
  featureModuleName,
  outputQueryDescription,
  outputQueryType,
  powerNetLevel,
  isActivePowerNetLevel,
  isSimulating,
  resetDelay,
  querySubType,
  queryPolarity,
  queryValue,
  queryTranslations,
  queryTranslationType,
  queryStartBit,
  queryNumberOfBits,
  pwmMode,
  circuit,
  featureType,
  mappedOutputId,
  floatingCircuit,
  simulatedFuseOpen,
}) => {
  const isPin = isPinOutput(type)
  const isXmcPin = featureModuleType === XMC && isPin
  const isXmcNative = featureModuleType === XMC && isNativeOutput(type)

  const pwmItems = [
    {
      label: strings.mode,
      value: pwmModeValue(pwmMode),
    },
    ...insertIf(pwmMode !== PWM_MODES_MAPPING.VARIABLE_DUTY_CYCLE, {
      label: strings.duty_cycle,
      value: dutyCycleValue(dutyCycle),
    }),
    ...insertIf(
      pwmMode === PWM_MODES_MAPPING.VARIABLE_DUTY_CYCLE,
      {
        label: strings.duty_cycle_min,
        value: dutyCycleValue(dutyCycleMin),
      },
      {
        label: strings.duty_cycle_max,
        value: dutyCycleValue(dutyCycleMax),
      }
    ),
    ...insertIf(pwmMode !== PWM_MODES_MAPPING.VARIABLE_FREQUENCY, {
      label: strings.frequency,
      value: frequencyValue(frequency),
    }),
    ...insertIf(
      pwmMode === PWM_MODES_MAPPING.VARIABLE_FREQUENCY,
      {
        label: strings.frequency_min,
        value: frequencyValue(frequencyMin),
      },
      {
        label: strings.frequency_max,
        value: frequencyValue(frequencyMax),
      }
    ),
  ]

  const xmcPinItems = [
    {
      label: strings.current,
      value: `${current}${strings.amps_a}`,
    },
    {
      label: strings.polarity,
      value: polarityLabels[polarity],
    },
    ...insertIf(polarity === PWM, ...pwmItems),
  ]

  const getCircuitText = () => {
    if (!mappedOutputId && !floatingCircuit) return strings.no_input_selected
    if (mappedOutputId && !circuit) return strings.no_input_available

    return circuit || strings.floating_circuit_label(floatingCircuit)
  }

  const outputValues = [
    ...insertIf(isCanOutput(type), {
      label: strings.bit_byte_pair,
      value: bitBytePairLabel(startBit, startBit + numberOfBits - 1),
    }),
    ...insertIf(isXmcPin, ...xmcPinItems),
    ...insertIf(
      current === AMPS20,
      {
        label: strings.virtual_fuse_setting,
        value: (
          <span>
            {virtualFuseMask(virtualFuseSetting)}{' '}
            {isSimulating && (
              <IconLayout>
                <EvaluationIconStyled
                  evaluation={!simulatedFuseOpen}
                  usePaletteStyles
                />
              </IconLayout>
            )}
          </span>
        ),
      },
      {
        label: strings.reset_virtual_fuse,
        value: virtualFuseReset(resetType, resetAttempts, resetDelay, true),
      }
    ),
    ...insertIf(
      isPin,
      {
        label: strings.keep_awake,
        value: keepAwakeLabel(isAwake),
      },
      {
        label: strings.power_net_level,
        value: (
          <span>
            {powerNetLevel} - {powerNetLevels(powerNetLevel)}
            {isSimulating && !isActivePowerNetLevel && (
              <StyledSimulatedOutputEvaluationBadge
                evaluation={false}
                useNodeStyles={false}
              />
            )}
            {isSimulating && isActivePowerNetLevel && (
              <StyledSimulatedOutputEvaluationBadge
                evaluation={true}
                useNodeStyles={false}
              />
            )}
          </span>
        ),
      },
      {
        label: strings.polarity,
        value: polarityLabels[polarity],
      }
    ),
    ...insertIf(
      isXmcNative && isDigitalInput(querySubType),
      {
        label: strings.polarity,
        value: conditionPolarityLabels[queryPolarity],
      },
      {
        label: strings.value,
        value: `${
          queryTranslations
            ? translationValue(
                queryValue,
                queryTranslationType,
                queryTranslations
              )
            : queryValue
        }`,
      }
    ),
    ...insertIf(isXmcNative && isCanInput(outputQueryType), {
      label: strings.bit_byte_pair,
      value: bitBytePairLabel(
        queryStartBit,
        queryStartBit + queryNumberOfBits - 1
      ),
    }),
    ...insertIf(isTimer(outputQueryType), {
      label: strings.evaluate_when,
      value: outputQueryDescription,
    }),
    ...insertIf(
      isPin &&
        FEATURE_MODULE_NAMES_WITH_HARNESSES.includes(featureModuleName) &&
        featureType === INTERNAL,
      {
        label: strings.circuit,
        value: getCircuitText(),
      }
    ),
  ]

  const unique = uniqBy(prop('label'), outputValues)

  return unique
}

const StyledSimulatedOutputEvaluationBadge = styled(EvaluationIconStyled)`
  float: right;
`

const IconLayout = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  float: right;
  && > svg {
    width: 100%;
    height: 100%;
  }
`
